import * as moment from "moment";

/** Maximum number of records submitted in a single import request. */
export const MAX_IMPORT_RECORDS = 2000;

export type LTAFieldKey =
  | "Title"
  | "LTANumber"
  | "LTAType"
  | "LTAService"
  | "LTAValue"
  | "Region"
  | "Contractor"
  | "LeadAgency"
  | "Agencies"
  | "UsableByOtherAgencies"
  | "StartDate"
  | "EndDate"
  | "AdditionalInformation";

export type FieldMapping = Partial<Record<LTAFieldKey, string>>;

export type ImportStatus = "idle" | "running" | "done";

export type ImportResults = {
  total: number;
  successful: number;
  failed: number;
  downloadUrl: string | null;
};

export const LTA_FIELDS: Array<{
  key: LTAFieldKey;
  label: string;
  required?: boolean;
  hint?: string;
}> = [
  { key: "Title", label: "LTA Title", required: true },
  { key: "LTANumber", label: "LTA Number", required: true },
  { key: "LTAType", label: "LTA Type", hint: "Must match an existing LTA Type" },
  {
    key: "LTAService",
    label: "LTA Service",
    hint: "Must match an existing LTA Service",
  },
  { key: "LTAValue", label: "LTA Value", hint: "Numbers only, e.g. 2000000" },
  {
    key: "Region",
    label: "Region",
    hint: "Separate multiple regions with |",
  },
  { key: "Contractor", label: "Contractor", required: true },
  { key: "LeadAgency", label: "Lead UN Agency", required: true },
  {
    key: "Agencies",
    label: "Other Agencies using this LTA",
    hint: "Separate multiple agencies with |",
  },
  {
    key: "UsableByOtherAgencies",
    label: "Usable by Other UN Agencies",
    hint: "Yes or No",
  },
  { key: "StartDate", label: "LTA Start Date", hint: "DD/MM/YYYY" },
  { key: "EndDate", label: "LTA End Date", required: true, hint: "DD/MM/YYYY" },
  { key: "AdditionalInformation", label: "Additional Information" },
];

function normalizeStr(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function similarityScore(a: string, b: string): number {
  if (a === b) return 2;
  if (a.includes(b) || b.includes(a)) {
    return Math.min(a.length, b.length) / Math.max(a.length, b.length);
  }
  return 0;
}

export function autoMapFields(csvHeaders: string[]): FieldMapping {
  const mapping: FieldMapping = {};
  const claimed = new Set<string>();

  for (const field of LTA_FIELDS) {
    // Match against the human label as well as the key, so a header of
    // "Lead UN Agency" finds the `LeadAgency` field.
    const candidates = [normalizeStr(field.key), normalizeStr(field.label)];
    let bestHeader = "";
    let bestScore = 0;

    for (const header of csvHeaders) {
      if (claimed.has(header)) continue;
      const normHeader = normalizeStr(header);
      const score = Math.max(
        ...candidates.map((c) => similarityScore(c, normHeader))
      );
      if (score > bestScore) {
        bestScore = score;
        bestHeader = header;
      }
    }

    if (bestScore > 0) {
      mapping[field.key] = bestHeader;
      claimed.add(bestHeader);
    }
  }

  return mapping;
}

const parseDate = (value: string | null) =>
  (value && moment(value, "DD/MM/YYYY").toDate().toLocaleDateString("en-CA")) ||
  null;

export function mapRowToRecord(
  row: Record<string, string>,
  mappings: FieldMapping
) {
  const get = (key: LTAFieldKey): string | null => {
    const col = mappings[key];
    if (!col) return null;
    return row[col]?.trim() || null;
  };

  return {
    title: get("Title") ?? "",
    ltaNumber: get("LTANumber") ?? "",
    ltaType: get("LTAType"),
    ltaService: get("LTAService"),
    ltaValue: get("LTAValue"),
    location: get("Region"),
    contractor: get("Contractor"),
    leadAgency: get("LeadAgency"),
    agencies: get("Agencies"),
    isUsableByOtherAgencies: get("UsableByOtherAgencies"),
    startDate: parseDate(get("StartDate")),
    endDate: parseDate(get("EndDate")),
    additionalInformation: get("AdditionalInformation"),
  };
}
